import Button from 'components/buttons/Button';
import { FormCard } from 'components/forms/FormComponents';
import useNeveForm from 'components/forms/NeveForm';
import TextAreaField from 'components/forms/TextAreaField';
import TextField from 'components/forms/TextField';
import { MainSectionWithGaps } from 'components/sections/MainSection';
import useModifyRequest from 'hooks/useModifyRequest';
import {
    createErrorMessage,
    createGlobalErrorFromAPIError,
    fieldErrorCodes,
    globalErrorCodes,
    requiredFieldErrorMessage,
} from 'shared/error-messages';
import styled from 'styled-components';
import strings from '../../strings/strings.json';
import useGetRequest from 'hooks/useGetRequest';
import { BodyRegular, BodySmall, Card, FlexCol, FlexEnd, FlexLg, Headline4 } from 'components/Elements';
import { logError } from 'lib/debug-helpers';
import React from 'react';
import Notification, { NotificationType } from 'components/notifiers/Notification';
import { breakpoints } from 'theme';
import Header from 'components/sections/Header';
import NavBackButton from 'components/buttons/NavBackButton';

type NotifyUsersFormInput = {
    header: string;
    body: string;
};

type UserDetails = {
    firstName: string;
    lastName: string;
    email: string;
};

export default function NotifyUsersPage() {
    const [errorMessage, setErrorMessage] = React.useState<string>('');
    const [success, setSuccess] = React.useState<boolean>(false);

    const { data: users } = useGetRequest<UserDetails[]>('admin/users');

    const { modifyData: sendNotifications, loading: sendingNotifications } = useModifyRequest(
        'admin/notify-users',
        'POST',
    );

    const {
        register,
        handleSubmit,
        formState: { errors },
        watch,
        reset,
    } = useNeveForm<NotifyUsersFormInput>({
        header: '',
        body: '',
    });

    async function onSubmit(formData: NotifyUsersFormInput) {
        const notificationResponse = await sendNotifications({ header: formData.header, body: formData.body });

        if (notificationResponse.errors) {
            const apiErrorMessage = createGlobalErrorFromAPIError(notificationResponse.errors[0].code);

            logError(apiErrorMessage);

            if (apiErrorMessage === globalErrorCodes.unknownError) {
                setErrorMessage(`${strings.notifyAllUsersPage.genericError}`);
            } else {
                setErrorMessage(apiErrorMessage);
            }

            return;
        }

        setSuccess(true);
        reset();
        return;
    }

    return (
        <>
            <Header
                title={strings.notifyAllUsersPage.pageHeader}
                aboveTitleElement={
                    <NavBackButton
                        backButtonTitle={strings.notifyAllUsersPage.backButtonDetails.title}
                        backButtonAria={strings.notifyAllUsersPage.backButtonDetails.aria}
                        backButtonHref={'/admin/dashboard'}
                    />
                }
            />
            <MainSectionWithGaps>
                <CardContainer>
                    <StyledFormCard onSubmit={handleSubmit(onSubmit)}>
                        <TextField
                            fieldName={strings.notifyAllUsersPage.messageHeaderTextField.inputLabel}
                            labelText={strings.notifyAllUsersPage.messageHeaderTextField.inputLabel}
                            inputAria={strings.notifyAllUsersPage.messageHeaderTextField.inputAria}
                            errorMessage={errors.header?.message}
                            maxLength={100}
                            {...register('header', {
                                required: {
                                    value: true,
                                    message: requiredFieldErrorMessage(
                                        `${strings.notifyAllUsersPage.messageHeaderTextField.requiredFieldErrorMessage}`,
                                    ),
                                },
                            })}
                        />
                        <TextAreaField
                            fieldName={strings.notifyAllUsersPage.messageBodyInputField.inputLabel}
                            aria={strings.notifyAllUsersPage.messageBodyInputField.inputAria}
                            charactersLeft={1000 - watch('body').length}
                            errorMessage={errors.body?.message}
                            maxLength={1000}
                            {...register('body', {
                                required: {
                                    value: true,
                                    message: requiredFieldErrorMessage(
                                        `${strings.notifyAllUsersPage.messageBodyInputField.requiredFieldErrorMessage}`,
                                    ),
                                },
                                maxLength: {
                                    value: 1000,
                                    message: createErrorMessage(
                                        `${strings.notifyAllUsersPage.messageBodyInputField.inputLabel}`,
                                        fieldErrorCodes.maxLength,
                                    ),
                                },
                            })}
                        />
                        {success && (
                            <Notification
                                notificationType={NotificationType.SUCCESS}
                                title={strings.notifyAllUsersPage.notificationSuccessMessage}
                                maxWidth="1270"
                            />
                        )}
                        {errorMessage && (
                            <Notification
                                notificationType={NotificationType.DANGER}
                                title={errorMessage}
                                maxWidth="1270"
                            />
                        )}
                        <ButtonContainer>
                            <Button
                                title={strings.notifyAllUsersPage.submitButton.title}
                                aria={strings.notifyAllUsersPage.submitButton.aria}
                                type="submit"
                                submitting={sendingNotifications}
                            />
                        </ButtonContainer>
                    </StyledFormCard>
                    <UserListCard>
                        <Headline4>{strings.notifyAllUsersPage.allUsersCard.title}</Headline4>
                        {users ? (
                            users.map((user) => {
                                return (
                                    <FlexCol>
                                        <BodyRegular $bold>
                                            {user.firstName} {user.lastName}
                                        </BodyRegular>
                                        <BodySmall>{user.email}</BodySmall>
                                    </FlexCol>
                                );
                            })
                        ) : (
                            <Notification
                                notificationType={NotificationType.DANGER}
                                title={strings.notifyAllUsersPage.allUsersCard.errorMessageTitle}
                                description={strings.notifyAllUsersPage.allUsersCard.errorMessageDescription}
                                maxWidth="300px"
                            />
                        )}
                    </UserListCard>
                </CardContainer>
            </MainSectionWithGaps>
        </>
    );
}

const CardContainer = styled(FlexLg)`
    justify-content: space-between;
    max-width: 100%;
    @media (max-width: ${breakpoints.lg}) {
        flex-direction: column;
    }
`;

const StyledFormCard = styled(FormCard)`
    max-height: min-content;
    flex-grow: 1;
`;

const UserListCard = styled(Card)`
    width: fit-content;
    height: fit-content;
    display: flex;
    flex-direction: column;
`;

const ButtonContainer = styled(FlexEnd)`
    align-items: center;
`;
